<template>
  <v-container fluid class="pa-6">
    <!-- Breadcrumbs -->
    <div class="soc-breadcrumbs-container">
      <Breadcrumbs />
    </div>

    <!-- Hero Header Card -->
    <v-card class="soc-hero-card mb-6" elevation="6">
      <div class="soc-hero-header">
        <div class="d-flex align-center">
          <v-avatar size="48" class="soc-hero-logo mr-4">
            <v-icon dark size="26">mdi-shield-check</v-icon>
          </v-avatar>
          <div>
            <h1 class="soc-hero-title">Subject of Certification</h1>
            <p class="soc-hero-subtitle mb-0">
              Define the scope and details of your certification assessment
            </p>
          </div>
        </div>
      </div>

      <!-- Step Progress -->
      <div class="soc-steps-bar">
        <div class="soc-step soc-step-active">
          <div class="soc-step-circle soc-step-circle-active">1</div>
          <span class="soc-step-label">Define a Subject of Certification</span>
        </div>
        <div class="soc-step-line"></div>
        <div class="soc-step">
          <div class="soc-step-circle">2</div>
          <span class="soc-step-label">Configure</span>
        </div>
        <div class="soc-step-line"></div>
        <div class="soc-step">
          <div class="soc-step-circle">3</div>
          <span class="soc-step-label">Review</span>
        </div>
      </div>
    </v-card>

    <!-- Form Card -->
    <v-row justify="center">
      <v-col cols="12" md="8" lg="6">
        <v-card class="soc-form-card" elevation="4">
          <v-form ref="serverForm" v-model="valid" lazy-validation>
            <v-card-text class="pa-6">
              <!-- Name Field -->
              <div class="soc-field-group mb-5">
                <div class="soc-field-label">
                  <v-icon small color="#1976d2" class="mr-1">mdi-tag-text</v-icon>
                  Certification Name
                </div>
                <v-text-field
                  v-model="certificationName"
                  label="Name"
                  outlined
                  dense
                  :rules="[rules.required, rules.duplicateName]"
                  :error-messages="
                    duplicateNameExists
                      ? 'Subject of Certification with this name already exists'
                      : ''
                  "
                  @input="checkDuplicateName"
                />
              </div>

              <!-- Description Field -->
              <div class="soc-field-group mb-5">
                <div class="soc-field-label">
                  <v-icon small color="#1976d2" class="mr-1">mdi-text-box</v-icon>
                  Description
                </div>
                <v-textarea
                  v-model="certificationDescription"
                  label="Description"
                  outlined
                  dense
                  rows="3"
                />
              </div>

              <!-- Certification Baseline Select -->
              <div class="soc-field-group mb-5">
                <div class="soc-field-label">
                  <v-icon small color="#0097a7" class="mr-1">mdi-certificate</v-icon>
                  Certification Baseline
                </div>
                <v-select
                  v-model="subjectOfCertification"
                  label="Please select Certification Baseline"
                  :items="standardsList"
                  item-text="displayName"
                  item-value="displayName"
                  outlined
                  dense
                  :loading="loadingStandards"
                  :readonly="isFromThreatSpider"
                />
              </div>

              <!-- Organizational Units (conditional) -->
              <div
                v-if="
                  subjectOfCertification === 'Organizational processes (aligned with IEC 62443-4-1)'
                "
                class="soc-field-group mb-5"
              >
                <div class="soc-field-label">
                  <v-icon small color="#388e3c" class="mr-1">mdi-domain</v-icon>
                  Organizational Units
                </div>
                <v-select
                  v-model="organizationalUnits"
                  :items="['R&D', 'Operations', 'Quality Assurance']"
                  label="Specify Organizational Unit(s) or Divisions in Scope"
                  outlined
                  dense
                />
              </div>
            </v-card-text>

            <!-- Action Buttons -->
            <v-divider></v-divider>
            <v-card-actions class="pa-5 d-flex justify-center">
              <v-btn class="soc-btn-clear mr-3" @click="clearForm">
                <v-icon left small>mdi-eraser</v-icon>
                Clear
              </v-btn>
              <v-btn class="soc-btn-cancel mr-3" @click="$router.go(-1)">
                <v-icon left small>mdi-arrow-left</v-icon>
                Cancel
              </v-btn>
              <v-btn
                class="soc-btn-next"
                :loading="loading"
                :disabled="duplicateNameExists"
                @click="handleNext"
              >
                Next
                <v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';
import socApi from '../api/soc';
import EventBus from '@/helpers/event-bus.js';
import { debounce } from 'lodash';

export default {
  name: 'SubjectOfCertification',
  components: {
    Breadcrumbs,
  },
  data() {
    return {
      loading: false,
      valid: false,
      subject: '',
      subjectOfCertification: '',
      certificationName: '',
      certificationDescription: '',
      organizationalUnits: '',
      selectedFamily: '',
      origin: 'manual',
      isFromThreatSpider: false,
      threatSpiderComponent: null,
      threatSpiderStandard: null,
      duplicateNameExists: false,
      checkingDuplicate: false,
      loadingStandards: false,
      standardsList: [],
      rules: {
        required: value => !!value || 'Required.',
        duplicateName: () =>
          !this.duplicateNameExists || 'Subject of Certification with this name already exists.',
      },
    };
  },
  created() {
    const componentQuery = this.$route.query.component;
    const standardQuery = this.$route.query.standard;

    if (componentQuery && standardQuery) {
      this.threatSpiderComponent = JSON.parse(componentQuery);
      this.threatSpiderStandard = JSON.parse(standardQuery);
      this.isFromThreatSpider = true;
      this.origin = 'spider';

      // Set values directly
      this.certificationName = `${this.threatSpiderComponent.ComponentName} - ${this.threatSpiderStandard.StandardName} Certification`;
      this.certificationDescription = `Certification of ${this.threatSpiderComponent.ComponentName} against ${this.threatSpiderStandard.StandardName} standard requirements`;

      // Map standard to subject selection
      const standardMapping = {
        'IEC 62443-4-2': 'Technical IACS component(s) (aligned with IEC 62443-4-2)',
        'IEC 62443-4-1': 'Organizational processes (aligned with IEC 62443-4-1)',
        'IEC 62443-2-4': 'IACS service provider aspects (aligned with IEC 62443-2-4)',
        'IEC 62443-3-3': 'IEC 62443-3-3',
        CRA: 'Cyber Resilience Act (CRA)',
      };

      // Find the matching standard and set it
      const matchingStandard = Object.entries(standardMapping).find(([key]) =>
        this.threatSpiderStandard.StandardName.includes(key),
      );

      if (matchingStandard) {
        this.subjectOfCertification = matchingStandard[1];
      }
    }

    this.debouncedCheckDuplicate = debounce(this.checkDuplicateName, 500);

    // Fetch standards list from the API
    this.fetchStandardsList();
  },
  methods: {
    async fetchStandardsList() {
      this.loadingStandards = true;
      try {
        const response = await this.$axios.get('/api/v1/standards-api/standards/list');
        this.standardsList = response.data;
        console.log('Standards list loaded:', this.standardsList);
      } catch (error) {
        console.error('Error fetching standards list:', error);
        // Fallback to hardcoded list if API fails
        this.standardsList = [
          {
            code: 'IEC-62443-4-1',
            displayName: 'Organizational processes (aligned with IEC 62443-4-1)',
          },
          {
            code: 'IEC-62443-4-2',
            displayName: 'Technical IACS component(s) (aligned with IEC 62443-4-2)',
          },
          {
            code: 'IEC-62443-2-4',
            displayName: 'IACS service provider aspects (aligned with IEC 62443-2-4)',
          },
          { code: 'IEC-62443-3-3', displayName: 'IEC 62443-3-3' },
          {
            code: 'Cyber Resilience Act (CRA 32024R2847)',
            displayName: 'Cyber Resilience Act (CRA)',
          },
        ];
      } finally {
        this.loadingStandards = false;
      }
    },

    async checkDuplicateName() {
      if (!this.certificationName || this.certificationName.trim() === '') {
        this.duplicateNameExists = false;
        return;
      }

      this.checkingDuplicate = true;
      try {
        const response = await socApi.checkDuplicateName(this.certificationName);
        this.duplicateNameExists = response.data;

        // Force form validation to update
        this.$nextTick(() => {
          if (this.$refs.serverForm) {
            this.$refs.serverForm.validate();
          }
        });
      } catch (error) {
        console.error('Error checking duplicate name:', error);
        this.duplicateNameExists = false;
      } finally {
        this.checkingDuplicate = false;
      }
    },

    handleNext() {
      if (!this.subjectOfCertification) {
        alert('Please select a Subject of Certification before proceeding.');
        return;
      }

      // Check for duplicates before proceeding
      if (this.duplicateNameExists) {
        EventBus.$emit('snackbar', {
          text: 'Subject of Certification with this name already exists',
          color: 'error',
        });
        return;
      }

      this.loading = true;

      setTimeout(() => {
        switch (this.subjectOfCertification) {
          case 'Organizational processes (aligned with IEC 62443-4-1)':
            this.$router.push({
              name: 'ListSpecifiedProcesses',
              params: { subject: this.subjectOfCertification },
              query: {
                name: this.certificationName,
                description: this.certificationDescription,
                family: 'IEC 62443-4-1',
                origin: this.origin,
              },
            });
            break;
          case 'Technical IACS component(s) (aligned with IEC 62443-4-2)':
            this.$router.push({
              name: 'TechnicalComponentsView',
              params: { subject: this.subjectOfCertification },
              query: {
                name: this.certificationName,
                description: this.certificationDescription,
                family: 'IEC 62443-4-2',
                origin: this.origin,
                threatSpiderComponent: this.threatSpiderComponent,
                threatSpiderStandard: this.threatSpiderStandard,
              },
            });
            break;
          case 'IACS service provider aspects (aligned with IEC 62443-2-4)':
            this.$router.push({
              name: 'ServiceProviderView',
              params: { subject: this.subjectOfCertification },
              query: {
                name: this.certificationName,
                description: this.certificationDescription,
                family: 'IEC 62443-2-4',
                origin: this.origin,
                threatSpiderComponent: this.threatSpiderComponent,
                threatSpiderStandard: this.threatSpiderStandard,
              },
            });
            break;
          case 'Cyber Resilience Act (CRA)':
            this.$router.push({
              name: 'Requirements',
              params: { subject: this.subjectOfCertification },
              query: {
                name: this.certificationName,
                description: this.certificationDescription,
                family: 'Cyber Resilience Act',
                origin: this.origin,
                threatSpiderComponent: this.threatSpiderComponent,
                threatSpiderStandard: this.threatSpiderStandard,
              },
            });
            break;
          case 'IEC 62443-3-3':
            this.$router.push({
              name: 'Requirements',
              params: { subject: this.subjectOfCertification },
              query: {
                name: this.certificationName,
                description: this.certificationDescription,
                family: 'IEC 62443-3-3',
                origin: this.origin,
                threatSpiderComponent: this.threatSpiderComponent,
                threatSpiderStandard: this.threatSpiderStandard,
              },
            });
            break;
        }

        this.loading = false;
      }, 1000);
    },
    clearForm() {
      // Reset form values
      this.certificationName = '';
      this.certificationDescription = '';
      this.subjectOfCertification = '';
      this.organizationalUnits = '';
      this.valid = false;
      this.origin = 'manual';
      this.duplicateNameExists = false;

      // Reset validation
      if (this.$refs.serverForm) {
        this.$refs.serverForm.resetValidation();
      }
    },
  },
};
</script>

<style scoped>
/* ============================================
   SUBJECT OF CERTIFICATION - Modern Design
   ============================================ */

.soc-breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 16px;
  padding-bottom: 28px;
}

.soc-breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* Hero Card */
.soc-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
  background: #ffffff;
}

.soc-hero-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 24px 28px;
}

.soc-hero-logo {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(4px);
}

.soc-hero-title {
  color: white;
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.soc-hero-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.92rem;
  font-weight: 400;
}

/* Step Progress Bar */
.soc-steps-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 28px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-top: 1px solid #e8eef3;
}

.soc-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.soc-step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
  background: #e0e0e0;
  color: #9e9e9e;
  transition: all 0.3s ease;
}

.soc-step-circle-active {
  background: linear-gradient(135deg, #00405e, #0097a7);
  color: white;
  box-shadow: 0 4px 14px rgba(0, 100, 148, 0.4);
}

.soc-step-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #78909c;
}

.soc-step-active .soc-step-label {
  color: #00405e;
}

.soc-step-line {
  flex: 1;
  max-width: 80px;
  height: 3px;
  background: linear-gradient(90deg, #e0e0e0, #bdbdbd);
  border-radius: 2px;
  margin: 0 12px;
  margin-bottom: 22px;
}

/* Form Card */
.soc-form-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
}

.soc-field-group {
  position: relative;
}

.soc-field-label {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: #455a64;
  margin-bottom: 6px;
  letter-spacing: 0.3px;
}

/* Action Buttons */
.soc-btn-clear {
  background: linear-gradient(135deg, #f37d21 0%, #ff9800 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 0 20px !important;
  box-shadow: 0 4px 12px rgba(243, 125, 33, 0.3) !important;
  transition: all 0.3s ease !important;
}

.soc-btn-clear:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(243, 125, 33, 0.5) !important;
}

.soc-btn-cancel {
  background: linear-gradient(135deg, #78909c 0%, #90a4ae 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 0 20px !important;
  box-shadow: 0 4px 12px rgba(120, 144, 156, 0.3) !important;
  transition: all 0.3s ease !important;
}

.soc-btn-cancel:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(120, 144, 156, 0.5) !important;
}

.soc-btn-next {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 0 24px !important;
  box-shadow: 0 4px 14px rgba(0, 100, 148, 0.4) !important;
  transition: all 0.3s ease !important;
}

.soc-btn-next:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 22px rgba(0, 100, 148, 0.6) !important;
}

/* Responsive */
@media (max-width: 960px) {
  .soc-hero-header {
    padding: 20px;
  }
  .soc-hero-title {
    font-size: 1.3rem;
  }
  .soc-steps-bar {
    padding: 16px;
  }
}
</style>
